import type { FC, FormEvent, ReactNode } from 'react'

import { useRouter } from 'next/router'
import { useState } from 'react'
import { ArrowRight, Link2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSession } from '@/hooks/use-session'

interface UrlFormProps {
    id: string
    dark?: boolean
    className?: string
}

// Starts a conversion from the home page, through sign up when needed.
export const UrlForm: FC<UrlFormProps> = ({ id, dark = false, className }): ReactNode => {
    const router = useRouter()
    const { user } = useSession()
    const [url, setUrl] = useState('')
    const [error, setError] = useState<string | null>(null)

    const submit = (event: FormEvent): void => {
        event.preventDefault()

        const value = url.trim()

        if (!value) {
            setError('Paste the link to one of your blog posts.')

            return
        }

        const normalized = /^https?:\/\//i.test(value) ? value : `https://${value}`

        try {
            new URL(normalized)
        }
        catch {
            setError('That does not look like a web address.')

            return
        }

        const target = `/create?url=${encodeURIComponent(normalized)}`

        void router.push(user ? target : `/signup?next=${encodeURIComponent(target)}`)
    }

    return (
        <form onSubmit={submit} className={cn('flex w-full flex-col gap-2', className)} noValidate>
            <label htmlFor={id} className='sr-only'>
                Blog post link
            </label>
            <div className={cn('flex flex-col gap-2 rounded-[28px] p-2 shadow-lg sm:flex-row sm:items-center sm:rounded-full', dark ? 'bg-paper/10 ring-1 ring-paper/20' : 'bg-card ring-1 ring-border')}>
                <div className='flex flex-1 items-center gap-2 px-3'>
                    <Link2 className={cn('size-5 shrink-0', dark ? 'text-paper/60' : 'text-muted-foreground')} />
                    <input
                        id={id}
                        type='url'
                        inputMode='url'
                        value={url}
                        onChange={event => {
                            setUrl(event.target.value)
                            setError(null)
                        }}
                        placeholder='https://yourblog.com/your-best-post'
                        aria-invalid={Boolean(error)}
                        aria-describedby={error ? `${id}-error` : undefined}
                        className={cn('h-12 w-full min-w-0 bg-transparent text-base outline-none', dark ? 'text-paper placeholder:text-paper/45' : 'placeholder:text-muted-foreground')}
                    />
                </div>
                <button type='submit' className='inline-flex h-12 shrink-0 items-center justify-center gap-2 rounded-full bg-primary px-6 text-base font-semibold text-primary-foreground transition-colors hover:bg-primary/90'>
                    Make my carousel
                    <ArrowRight className='size-5' />
                </button>
            </div>
            {error && (
                <p id={`${id}-error`} role='alert' className={cn('px-4 text-sm', dark ? 'text-coral' : 'text-destructive')}>
                    {error}
                </p>
            )}
        </form>
    )
}
